import {
  InsurancePolicyCoverageTypes,
  OwnerOccupancyCode,
  PetCoverage,
  ResidentialCoverage,
  VehicleCoverage,
} from '@/interfaces';
import {
  isComprehensiveCarOrIsFireTheftAndThirdPartyCarOrIsThirdPartyPropertyDamageCar,
  MotorAdditionalBenefits,
} from '@/pages/my-racq/insurance/product-details/PolicyDetailsMotorCoverageMapper';
import {
  CommonConfigSettings,
  SettingCategory,
  useConfigSettingsStore,
} from '@/store/common-config-settings/config-settings.state';
/**
 * Determines whether to show the `Building sum insured > Sum insured` item.
 * SSINSP.FR7.3
 */
export const showBuildingSumInsured = (coverage: ResidentialCoverage): boolean => {
  const coverageType = coverage?.coverageType?.toString();
  const isHome = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.Home];
  const isHomeAndContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.HomeAndContents];

  if (isHome || isHomeAndContents) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Mortgagee` item.
 * SSINSP.FR7.2
 */
export const showMortgagee = (coverage: ResidentialCoverage): boolean => {
  if (coverage.mortgagee && coverage.mortgagee.length > 0) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Contents sum insured > Mobile phones` item.
 * SSINSP.FR7.4
 */
export const showContentsSumInsuredMobilePhones = (coverage: ResidentialCoverage): boolean => {
  const coverageType = coverage?.coverageType?.toString();
  const isContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.Contents];
  const isHomeAndContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.HomeAndContents];
  const hasMobilePhones = coverage.mobilePhones && coverage.mobilePhones.length > 0;

  if ((isContents || isHomeAndContents) && hasMobilePhones) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Contents sum insured > Grouped items away from home` item.
 * SSINSP.FR7.4
 */
export const showContentsSumInsuredGroupedItemsAwayFromHome = (coverage: ResidentialCoverage): boolean => {
  const coverageType = coverage?.coverageType?.toString();
  const isContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.Contents];
  const isHomeAndContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.HomeAndContents];
  const isOwnerOccupied = (coverage.ownerOccupancyCode as OwnerOccupancyCode) === OwnerOccupancyCode.OWNEROCC;
  const isRenter = (coverage.ownerOccupancyCode as OwnerOccupancyCode) === OwnerOccupancyCode.TENANTPERM;

  if ((isContents || isHomeAndContents) && (isOwnerOccupied || isRenter)) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Contents sum insured > Individual items away from home` item.
 * SSINSP.FR7.4
 */
export const showContentsSumInsuredIndividualItemsAwayFromHome = (coverage: ResidentialCoverage): boolean => {
  const coverageType = coverage?.coverageType?.toString();
  const isContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.Contents];
  const isHomeAndContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.HomeAndContents];
  const hasItemsAwayFromHome = coverage.portableItems && coverage.portableItems?.length > 0;

  if ((isContents || isHomeAndContents) && hasItemsAwayFromHome) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Contents sum insured > Specified items` item.
 * SSINSP.FR7.4
 */
export const showContentsSumInsuredSpecifiedItems = (coverage: ResidentialCoverage): boolean => {
  const coverageType = coverage?.coverageType?.toString();
  const isContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.Contents];
  const isHomeAndContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.HomeAndContents];
  const hasSpecifiedItems = coverage.homeItems && coverage.homeItems.length > 0;

  if ((isContents || isHomeAndContents) && hasSpecifiedItems) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Contents sum insured > General contents` item.
 * SSINSP.FR7.4
 */
export const showContentsSumInsuredGeneralContents = (coverage: ResidentialCoverage): boolean => {
  const coverageType = coverage?.coverageType?.toString();
  const isContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.Contents];
  const isHomeAndContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.HomeAndContents];

  if (isContents || isHomeAndContents) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Contents sum insured > Total contents` item.
 * SSINSP.FR7.4
 */
export const showContentsSumInsuredTotalContents = (coverage: ResidentialCoverage): boolean => {
  const coverageType = coverage?.coverageType?.toString();
  const isContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.Contents];
  const isHomeAndContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.HomeAndContents];

  if (isContents || isHomeAndContents) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Applicable excesses > Building excess` item.
 * SSINSP.FR7.5
 */
export const showApplicableExcessesBuildingExcess = (coverage: ResidentialCoverage): boolean => {
  const coverageType = coverage?.coverageType?.toString();
  const isHome = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.Home];
  const isHomeAndContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.HomeAndContents];

  if (isHome || isHomeAndContents) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Applicable excesses > Contents excess` item.
 * SSINSP.FR7.5
 */
export const showApplicableExcessesContentsExcess = (coverage: ResidentialCoverage): boolean => {
  const coverageType = coverage?.coverageType?.toString();
  const isContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.Contents];
  const isHomeAndContents = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.HomeAndContents];

  if (isContents || isHomeAndContents) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Optional benefits > Advanced cover` item.
 * SSINSP.FR7.6
 */
export const showOptionalBenefitsAdvancedCover = (coverage: ResidentialCoverage): boolean => {
  const hasBuildingAdvancedCover = coverage.hasBuildingAdvancedCover;
  const hasContentsAdvancedCover = coverage.hasContentsAdvancedCover;
  const isLandlord = (coverage.ownerOccupancyCode as OwnerOccupancyCode) === OwnerOccupancyCode.LANDLORD;

  if ((hasBuildingAdvancedCover || hasContentsAdvancedCover) && !isLandlord) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Optional benefits > Motor burnout` item.
 * SSINSP.FR7.6
 */
export const showOptionalBenefitsMotorBurnout = (coverage: ResidentialCoverage): boolean => {
  const hasBuildingMotorBurnout = coverage.hasBuildingMotorBurnout;
  const hasContentsMotorBurnout = coverage.hasContentsMotorBurnout;

  if (hasBuildingMotorBurnout || hasContentsMotorBurnout) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Optional benefits > Pet cover` item.
 * SSINSP.FR7.6
 */
export const showOptionalBenefitsPetCover = (coverage: ResidentialCoverage): boolean => {
  if (coverage.petsCovered && coverage.petsCovered > 0) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Optional benefits > Small business contents` item.
 * SSINSP.FR7.6
 */
export const showOptionalBenefitsSmallBusinessContents = (coverage: ResidentialCoverage): boolean => {
  if (coverage.hasSmallBusinessContentsCover) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the `Optional benefits > Increased landlord fixtures & fittings` item.
 * SSINSP.FR7.6
 */
export const showOptionalBenefitsIncreasedLandlordFixturesAndFittings = (coverage: ResidentialCoverage): boolean => {
  if (coverage.landlordFixturesAndFittings && coverage.landlordFixturesAndFittings > 0) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the Finance field.
 * SSINSP.FR8.
 */
export const showFinance = (coverage: VehicleCoverage): boolean => {
  if (
    isComprehensiveCarOrIsFireTheftAndThirdPartyCarOrIsThirdPartyPropertyDamageCar(coverage) &&
    coverage.isVehicleUsePrivate &&
    coverage.typeOfFinanceCode?.toLowerCase() !== 'nofinance' &&
    coverage.finance !== null &&
    coverage.finance.length > 0
  ) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the Motor standard items (vehicle location, usage, drivers, sum insured).
 * SSINSP.FR8.4
 */
export const showStandardPolicyDetails = (coverage: VehicleCoverage): boolean => {
  if (isComprehensiveCarOrIsFireTheftAndThirdPartyCarOrIsThirdPartyPropertyDamageCar(coverage)) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show Motor sum insured.
 * SSINSP.FR8.4
 */
export const showSumInsured = (coverage: VehicleCoverage): boolean => {
  if (
    isComprehensiveCarOrIsFireTheftAndThirdPartyCarOrIsThirdPartyPropertyDamageCar(coverage) &&
    (coverage.agreedValue || coverage.sumInsured || coverage.isMarketValue)
  ) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the High Performance excess.
 * SSINSP.FR8.5
 */
export const showHighPerformanceExcess = (coverage: VehicleCoverage): boolean => {
  if (
    isComprehensiveCarOrIsFireTheftAndThirdPartyCarOrIsThirdPartyPropertyDamageCar(coverage) &&
    coverage.highPerformanceExcess &&
    coverage.highPerformanceExcess > 0
  ) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the Additional hire car.
 * SSINSP.FR8.6
 */
export const showAdditionalHireCar = (coverage: VehicleCoverage): boolean => {
  const coverageType = coverage?.coverageType?.toString();
  const isComprehensiveCar =
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.MotorComprehensive];
  const hasHireCar = coverage.benefits
    .map((item) => item.toLowerCase())
    .includes(MotorAdditionalBenefits.FreeHireCar.toLowerCase());

  if (isComprehensiveCar && hasHireCar) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the Excess free glass repair.
 * SSINSP.FR8.6
 */
export const showExcessFreeGlassRepair = (coverage: VehicleCoverage): boolean => {
  const coverageType = coverage?.coverageType?.toString();
  const isComprehensiveCar =
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.MotorComprehensive];
  const hasWindscreen = coverage.benefits
    .map((item) => item.toLowerCase())
    .includes(MotorAdditionalBenefits.Windscreen.toLowerCase());

  if (isComprehensiveCar && hasWindscreen) {
    return true;
  }
  return false;
};

const convertYYYYMMDDToDate = (dateNumber: number) => {
  const dateString = String(dateNumber); // Convert to string: "20251010"

    // Slice the components
    const  year = dateString.slice(0, 4); // "2025"
    const month = dateString.slice(4, 6); // "10"
    const day = dateString.slice(6, 8); // "10"

    // Create the Date object: Note the -1 on the month
    const NCDPRemovalDateObject = new Date(
      Number(year),
      Number(month) - 1,
      Number(day)
    );
    return NCDPRemovalDateObject;
}

export const showNoClaimDiscountProtection = async (coverage: VehicleCoverage): Promise<boolean> => {
  //Get NCDP Removal Date from Sitecore Config
  const commonConfigSettingsStore = useConfigSettingsStore();
  const NCDPRemovalDate = await commonConfigSettingsStore.getSetting(
    CommonConfigSettings.NCDPRemovalDate,
    SettingCategory.CommonSettings
  );
  if (typeof NCDPRemovalDate !== 'number' ) return false;
  if (coverage.riskEffectiveDate === null) return false;
  const NCDPRemovalDateObject = convertYYYYMMDDToDate(NCDPRemovalDate);

  //showNoClaimDiscountProtection logic starts
  const coverageType = coverage?.coverageType?.toString();
  const isComprehensiveCar =
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.MotorComprehensive];
  const hasNCDProtections = coverage.benefits
    .map((item) => item.toLowerCase())
    .includes(MotorAdditionalBenefits.Rating1Protection.toLowerCase());

  if ((coverage.riskEffectiveDate < NCDPRemovalDateObject) && isComprehensiveCar && hasNCDProtections) {
    return true;
  }
  return false;
};

/**
 * Determines whether to show the Applicable excess items.
 * SSINSP.FR8.6
 */
export const showApplicableExcesses = (coverage: VehicleCoverage): boolean => {
  return isComprehensiveCarOrIsFireTheftAndThirdPartyCarOrIsThirdPartyPropertyDamageCar(coverage);
};

export const showTenderLovingCare = (coverage: PetCoverage): boolean => {
  return coverage.hasTlcCover;
};

/**
 * Determines whether to show the `Your discounts` item.
 * SSINSP.FR7.8
 */
export const showYourDiscounts = (coverage: ResidentialCoverage | VehicleCoverage | PetCoverage): boolean => {
  if (coverage.discounts.length > 0) {
    return true;
  }
  return false;
};

export const SSInsuranceProductDetailsPolicyDetailsRules = {
  //////////// HomeAndContents ////////////
  // Building sum insured
  showBuildingSumInsured,
  showMortgagee,

  // Contents sum insured
  showContentsSumInsuredMobilePhones,
  showContentsSumInsuredGroupedItemsAwayFromHome,
  showContentsSumInsuredIndividualItemsAwayFromHome,
  showContentsSumInsuredSpecifiedItems,
  showContentsSumInsuredGeneralContents,
  showContentsSumInsuredTotalContents,

  // Applicable excesses
  showApplicableExcessesBuildingExcess,
  showApplicableExcessesContentsExcess,

  // Optional benefits
  showOptionalBenefitsAdvancedCover,
  showOptionalBenefitsMotorBurnout,
  showOptionalBenefitsPetCover,
  showOptionalBenefitsSmallBusinessContents,
  showOptionalBenefitsIncreasedLandlordFixturesAndFittings,
  //////////// HomeAndContents ////////////

  //////////// Motor ////////////
  showFinance,
  showStandardPolicyDetails,
  showHighPerformanceExcess,
  showApplicableExcesses,
  showSumInsured,
  showAdditionalHireCar,
  showExcessFreeGlassRepair,
  showNoClaimDiscountProtection,
  //////////// Motor ////////////

  //////////// Pet ////////////
  showTenderLovingCare,
  //////////// Pet ////////////

  // Your discounts
  showYourDiscounts,
};
